/* aw-affiliate-ref.js
 * Code-independent affiliate attribution capture. Built 2026-07-20.
 *
 * WHY. Roughly 90% of this store's orders carry no discount code, and the two
 * affiliate platforms in use only reliably credit an order when the customer
 * uses that affiliate's own coupon. A customer who arrives on an affiliate link
 * and then checks out with one of the theme's first-order codes (starter22 /
 * home77 / family33) - or with no code at all - is invisible to coupon-based
 * reporting. Verified live on 2026-07-20: order #341749 arrived on LeadDyno
 * affiliate LUKE's link and checked out on family33, and three orders from
 * affiliate "1ka" ($511.72) carried no code at all. All four were uncredited.
 *
 * WHAT. Capture the affiliate reference on landing, keep it in localStorage, and
 * hand it to the cart as an attribute at add to cart so it lands permanently on
 * the order. localStorage is deliberate: Safari ITP caps script-written cookies
 * at ~7 days, ~82% of this store's traffic is mobile, and observed consideration
 * windows run to 20 days - a cookie alone loses the very journeys worth
 * attributing.
 *
 * BOTH first touch and last touch are recorded and BOTH are always emitted.
 * Which one earns commission is a business rule, not a code change - keeping
 * both means the rule can be decided (and revised) later without having thrown
 * the data away.
 *
 * SIGNAL RANKING. A param touch (?afmc= / ?sca_ref= / affiliate UTMs) outranks a
 * bare referrer touch. Creator link-in-bio hosts are captured only as a fallback
 * and are always prefixed "referrer:", because the store's OWN @alivewaters bio
 * link lives on one of those hosts - without ranking, our own link-in-bio would
 * claim first touch and outrank a genuine affiliate. A param touch therefore
 * UPGRADES a referrer-only first touch. Referrer rows are a lead, not a payable.
 *
 * SCOPE. This RECORDS attribution. It does not apply a discount, does not change
 * any price, and does not pay commission. Reconciliation against LeadDyno /
 * UpPromote stays a reporting step.
 *
 * PARITY. This file must be byte-identical in both A/B arms, and both arms must
 * write the same attribute names, or the experiment readout is confounded.
 */
(function () {
  var STORE_KEY = 'aw_affiliate_ref';
  var TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
  var MAX_LEN = 64;

  /* Affiliate codes on both platforms fit this charset. Anything else is a
   * malformed or hostile URL value and is rejected outright rather than
   * truncated - truncation could collide two distinct affiliate codes, and the
   * value ends up in a Shopify order attribute that humans read. */
  var VALID = /^[A-Za-z0-9._~-]+$/;

  /* Referral params by platform. afmc is LeadDyno (confirmed from a live
   * LeadDyno link on this store); sca_ref is UpPromote's. The generic pair are
   * kept because partner links in the wild use them. */
  var PARAMS = [
    ['afmc', 'leaddyno'],
    ['sca_ref', 'uppromote'],
    ['ref', 'ref'],
    ['aff', 'aff']
  ];

  /* Creator link-in-bio hosts. Ambassadors frequently post a bare alivewaters.com
   * URL from these, so no param exists and the referrer is the only signal. */
  var CREATOR_HOSTS = /(^|\.)(linktr\.ee|beacons\.ai|linkin\.bio|stan\.store|komi\.io|milkshake\.app|withkoji\.com|snipfeed\.co|bio\.site|taplink\.cc)$/i;

  function drop() {
    try { localStorage.removeItem(STORE_KEY); } catch (_) {}
  }

  /* Returns a validated value, or '' if unusable. Callers treat '' as no detection. */
  function clean(v) {
    var s = String(v).trim();
    if (!s || s.length > MAX_LEN) return '';
    return VALID.test(s) ? s : '';
  }

  function readStore() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      // Fail closed: a record with no first touch or no timestamp could never
      // expire, so treat it as corrupt and clear it.
      if (!v || !v.first || !v.firstAt) { drop(); return null; }

      var now = Date.now();
      var firstAged = now - v.firstAt > TTL_MS;
      var lastAged = !v.lastAt || now - v.lastAt > TTL_MS;
      if (firstAged && lastAged) { drop(); return null; }
      if (firstAged) {
        // First touch aged out but a later touch is still live - promote it
        // rather than discarding a fresh, usable attribution.
        v.first = v.last;
        v.firstAt = v.lastAt;
        v.firstKind = v.lastKind;
      }
      return v;
    } catch (_) { drop(); return null; }
  }

  function writeStore(v) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (_) {}
  }

  /* Read an affiliate reference off the current URL, else off the referrer.
   * Returns {value: "platform:code", kind: "param"|"referrer"} or null. */
  function detect() {
    var q;
    try { q = new URLSearchParams(location.search); } catch (_) { return null; }

    for (var i = 0; i < PARAMS.length; i++) {
      var c = clean(q.get(PARAMS[i][0]) || '');
      if (c) return { value: PARAMS[i][1] + ':' + c, kind: 'param' };
    }

    /* LeadDyno's own links also carry utm_source=leaddyno&utm_medium=affiliate
     * with the affiliate code in utm_campaign, which is how these orders are
     * already identifiable in Shopify's journey data. */
    var us = (q.get('utm_source') || '').toLowerCase();
    var um = (q.get('utm_medium') || '').toLowerCase();
    if (us === 'leaddyno' || um === 'affiliate') {
      var uc = clean(q.get('utm_campaign') || '');
      if (uc) return { value: (clean(us) || 'affiliate') + ':' + uc, kind: 'param' };
    }

    try {
      if (document.referrer) {
        var h = clean(new URL(document.referrer).hostname);
        if (h && CREATOR_HOSTS.test(h)) return { value: 'referrer:' + h, kind: 'referrer' };
      }
    } catch (_) {}

    return null;
  }

  var found = detect();
  if (found) {
    var s = readStore();
    var now = Date.now();
    if (!s) {
      s = {
        first: found.value, firstAt: now, firstKind: found.kind,
        last: found.value, lastAt: now, lastKind: found.kind
      };
    } else {
      // A real affiliate param upgrades a referrer-only first touch, so our own
      // link-in-bio can never outrank a genuine affiliate link.
      if (s.firstKind === 'referrer' && found.kind === 'param') {
        s.first = found.value;
        s.firstAt = now;
        s.firstKind = 'param';
      }
      s.last = found.value;
      s.lastAt = now;
      s.lastKind = found.kind;
    }
    writeStore(s);
  }

  /* Read API for the add-to-cart handlers in both arms. Both keys are ALWAYS
   * emitted (even when identical) so downstream reporting can rely on the shape.
   * Returns {} when there is nothing to report, so callers can merge blindly. */
  window.awAffiliateAttrs = function () {
    var v = readStore();
    if (!v || !v.first) return {};
    return {
      'Affiliate Ref': v.first,
      'Affiliate Ref Last': v.last || v.first
    };
  };
})();
